import { useMemo } from 'react'

/**
 * Compute actual graph depth via BFS from root nodes (no incoming edges).
 * This is more reliable than levelIndex which can be -1 for unmatched levels.
 */
function computeNodeDepths (nodes, edges) {
  const childrenMap = new Map()
  const incomingCount = new Map()

  for (const node of nodes) {
    childrenMap.set(node.id, [])
    incomingCount.set(node.id, 0)
  }

  for (const edge of edges) {
    if (!childrenMap.has(edge.source) || !incomingCount.has(edge.target)) continue

    childrenMap.get(edge.source).push(edge.target)
    incomingCount.set(edge.target, incomingCount.get(edge.target) + 1)
  }

  const depthMap = new Map()
  const queue = []

  for (const node of nodes) {
    if ((incomingCount.get(node.id) ?? 0) === 0) {
      depthMap.set(node.id, 0)
      queue.push(node.id)
    }
  }

  while (queue.length > 0) {
    const current = queue.shift()
    const currentDepth = depthMap.get(current)
    for (const childId of (childrenMap.get(current) ?? [])) {
      if (!depthMap.has(childId) || depthMap.get(childId) > currentDepth + 1) {
        depthMap.set(childId, currentDepth + 1)
        queue.push(childId)
      }
    }
  }

  return depthMap
}

/**
 * Build a map of parentNodeId -> [hidden child nodes] for nodes
 * whose computed depth exceeds maxVisibleDepth.
 */
function buildHiddenChildrenMap (nodes, edges, depthMap, maxVisibleDepth) {
  const nodeMap = new Map(nodes.map(n => [n.id, n]))
  const hidden = new Map()

  for (const edge of edges) {
    const targetDepth = depthMap.get(edge.target)
    const sourceDepth = depthMap.get(edge.source)
    if (targetDepth == null || sourceDepth == null) continue

    if (targetDepth > maxVisibleDepth && sourceDepth <= maxVisibleDepth) {
      if (!hidden.has(edge.source)) hidden.set(edge.source, [])
      const childNode = nodeMap.get(edge.target)
      if (childNode) hidden.get(edge.source).push(childNode)
    }
  }

  return hidden
}

/**
 * Filter nodes/edges by maxVisibleDepth using BFS-computed graph depth.
 *
 * Returns { visibleNodes, visibleEdges, hiddenChildrenMap }.
 * When maxVisibleDepth is null/undefined, everything passes through.
 */
export default function useDepthFilteredGraph (nodes, edges, maxVisibleDepth) {
  return useMemo(() => {
    if (maxVisibleDepth == null || !nodes?.length) {
      return {
        visibleNodes: nodes ?? [],
        visibleEdges: edges ?? [],
        hiddenChildrenMap: new Map()
      }
    }

    const depthMap = computeNodeDepths(nodes, edges)
    const hiddenChildrenMap = buildHiddenChildrenMap(nodes, edges, depthMap, maxVisibleDepth)

    const visibleNodeIds = new Set()
    const visibleNodes = []
    for (const node of nodes) {
      const depth = depthMap.get(node.id)
      if (depth != null && depth <= maxVisibleDepth) {
        visibleNodeIds.add(node.id)
        visibleNodes.push(node)
      }
    }

    const visibleEdges = (edges ?? []).filter(
      edge => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    )

    return { visibleNodes, visibleEdges, hiddenChildrenMap }
  }, [nodes, edges, maxVisibleDepth])
}
